import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Rocket, 
  Clock, 
  CheckCircle2, 
  Lightbulb,
  Zap,
  Brain,
  FileText,
  Shield,
  Calendar,
  TrendingUp,
  Mic,
  DollarSign,
  Truck,
  BarChart3,
  Users,
  Sparkles,
  Smartphone
} from 'lucide-react';

// Icon mapping from string to component
const ICON_MAP = {
  FileText,
  Shield,
  Calendar,
  Brain,
  TrendingUp,
  Mic,
  DollarSign,
  Truck,
  Users,
  BarChart3,
  Smartphone,
  Lightbulb,
  Rocket,
  Zap,
  Sparkles
};

// Default roadmap items - used only if database is empty
const DEFAULT_ROADMAP_ITEMS = [
  {
    id: 'docket-ocr',
    title: 'Docket OCR / Scanning',
    description: 'Scan paper dockets with your phone camera. AI extracts date, customer, equipment, hours, and descriptions automatically.',
    status: 'released',
    phase: 'released',
    category: 'ai',
    icon: 'FileText',
    value: 'Reduce invoice turnaround from days to hours',
    eta: 'Released'
  },
  {
    id: 'compliance-alerts',
    title: 'Compliance AI Alerts',
    description: 'Automatic alerts when operator licenses, equipment certifications, or insurance documents are expiring.',
    status: 'released',
    phase: 'released',
    category: 'ai',
    icon: 'Shield',
    value: 'Never miss an expiring ticket or certification',
    eta: 'Released'
  },
  {
    id: 'smart-operator-suggestions',
    title: 'Smart Operator Suggestions',
    description: 'AI suggests the best available operators based on certifications, experience, location proximity, and customer preferences.',
    status: 'released',
    phase: 'released',
    category: 'ai',
    icon: 'Users',
    value: 'Reduce scheduling time by 80%',
    eta: 'Released'
  },
  {
    id: 'quoting',
    title: 'Quoting System',
    description: 'Create professional quotes with equipment rates, labour costs, and charges. Convert approved quotes directly into jobs.',
    status: 'planned',
    phase: 'medium',
    category: 'core',
    icon: 'FileText',
    value: 'Streamline quote-to-job workflow',
    eta: 'Q1 2025'
  },
  {
    id: 'mobile-pwa',
    title: 'Mobile PWA App',
    description: 'Install EQUIPR CORE on your mobile home screen for native app-like experience. Works offline for docket entry.',
    status: 'released',
    phase: 'released',
    category: 'mobile',
    icon: 'Smartphone',
    value: 'Access EQUIPR CORE anywhere, even offline',
    eta: 'Released'
  }
];

const ProductRoadmap = () => {
  const { api } = useAuth();
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const fetchRoadmap = useCallback(async () => {
    try {
      const response = await api.get('/roadmap');
      if (response.data && response.data.length > 0) {
        setRoadmapItems(response.data);
      } else {
        // Use defaults if no items in database
        setRoadmapItems(DEFAULT_ROADMAP_ITEMS);
      }
    } catch (error) {
      console.error('Failed to fetch roadmap:', error);
      // Fallback to defaults on error
      setRoadmapItems(DEFAULT_ROADMAP_ITEMS);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchRoadmap();
  }, [fetchRoadmap]);

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || FileText;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'released':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle2 className="h-3 w-3 mr-1" />Released</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Zap className="h-3 w-3 mr-1" />In Progress</Badge>;
      case 'planned':
      default:
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Clock className="h-3 w-3 mr-1" />Planned</Badge>;
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'ai':
        return <Badge variant="outline" className="border-purple-300 text-purple-700"><Brain className="h-3 w-3 mr-1" />AI-Powered</Badge>;
      case 'analytics':
        return <Badge variant="outline" className="border-blue-300 text-blue-700"><BarChart3 className="h-3 w-3 mr-1" />Analytics</Badge>;
      case 'mobile':
        return <Badge variant="outline" className="border-green-300 text-green-700"><Smartphone className="h-3 w-3 mr-1" />Mobile</Badge>;
      case 'core':
      default:
        return <Badge variant="outline" className="border-gray-300"><Rocket className="h-3 w-3 mr-1" />Core</Badge>;
    }
  };

  const filterItems = (phase) => {
    if (phase === 'all') return roadmapItems;
    return roadmapItems.filter(item => item.phase === phase);
  };

  // Group released items and upcoming items
  const releasedItems = roadmapItems.filter(item => item.status === 'released');
  const upcomingItems = roadmapItems.filter(item => item.status !== 'released');
  const shortTermItems = roadmapItems.filter(item => item.phase === 'short');
  const mediumTermItems = roadmapItems.filter(item => item.phase === 'medium');
  const longTermItems = roadmapItems.filter(item => item.phase === 'long');

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="grid grid-cols-3 gap-4 mt-6">
            {[1,2,3].map(i => <div key={i} className="h-48 bg-muted rounded"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const RoadmapCard = ({ item }) => {
    const IconComponent = getIcon(item.icon);
    return (
      <Card className="h-full hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between mb-2">
            <div className="p-2 bg-[#ffd108]/20 rounded-lg">
              <IconComponent className="h-5 w-5 text-[#ffd108]" />
            </div>
            <div className="flex flex-col items-end gap-1">
              {getStatusBadge(item.status)}
              {getCategoryBadge(item.category)}
            </div>
          </div>
          <CardTitle className="text-base font-heading">{item.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-sm mb-3">{item.description}</CardDescription>
          {item.value && (
            <div className="flex items-center gap-2 text-sm text-green-600 font-medium mb-2">
              <Sparkles className="h-4 w-4" />
              {item.value}
            </div>
          )}
          {item.eta && item.status !== 'released' && (
            <div className="text-xs text-muted-foreground">
              Expected: {item.eta}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-[#ffd108] rounded-lg">
          <Rocket className="h-6 w-6 text-black" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold">Product Roadmap</h1>
          <p className="text-muted-foreground">See what's coming next to EQUIPR CORE</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{releasedItems.length}</p>
                <p className="text-xs text-muted-foreground">Released</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{shortTermItems.length}</p>
                <p className="text-xs text-muted-foreground">Short Term</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{mediumTermItems.length}</p>
                <p className="text-xs text-muted-foreground">Medium Term</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{roadmapItems.filter(i => i.category === 'ai').length}</p>
                <p className="text-xs text-muted-foreground">AI Features</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Roadmap Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({roadmapItems.length})</TabsTrigger>
          <TabsTrigger value="released">Released ({releasedItems.length})</TabsTrigger>
          <TabsTrigger value="short">Short Term ({shortTermItems.length})</TabsTrigger>
          <TabsTrigger value="medium">Medium Term ({mediumTermItems.length})</TabsTrigger>
          <TabsTrigger value="long">Long Term ({longTermItems.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {roadmapItems.length === 0 ? (
            <div className="text-center py-12">
              <Rocket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No roadmap items yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roadmapItems.map(item => (
                <RoadmapCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="released" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {releasedItems.map(item => (
              <RoadmapCard key={item.id} item={item} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="short" className="mt-6">
          {shortTermItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No short term items</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shortTermItems.map(item => (
                <RoadmapCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="medium" className="mt-6">
          {mediumTermItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No medium term items</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mediumTermItems.map(item => (
                <RoadmapCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="long" className="mt-6">
          {longTermItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No long term items</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {longTermItems.map(item => (
                <RoadmapCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Legend */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-sm font-heading">Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Released</Badge>
              <span className="text-sm text-muted-foreground">Available now</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-800"><Zap className="h-3 w-3 mr-1" />In Progress</Badge>
              <span className="text-sm text-muted-foreground">Currently being built</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" />Planned</Badge>
              <span className="text-sm text-muted-foreground">On the roadmap</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-purple-300 text-purple-700"><Brain className="h-3 w-3 mr-1" />AI-Powered</Badge>
              <span className="text-sm text-muted-foreground">Uses artificial intelligence</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductRoadmap;
